import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Wand2,
  FileText,
  Mail,
  MessageCircle,
  Megaphone,
  Video,
  Save,
  RotateCcw,
  Info,
  Copy,
  Check,
  AlertCircle,
  Plus,
  Trash2,
  ChevronRight,
  Search,
  X,
  Globe,
  ChevronDown,
  Sparkles,
  Loader2,
  User,
  Image,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { Navigate, useNavigate } from 'react-router-dom';
import { FUNNEL_STRATEGIES } from '@/config/funnelStrategies';

const CATEGORY_ICONS = {
  landing: FileText,
  email: Mail,
  whatsapp: MessageCircle,
  ad_copy: Megaphone,
  creative: Image,
  analysis: Sparkles,
  video_script: Video,
  avatar_script: User,
  brief_guide: Sparkles,
};

const CATEGORY_COLORS = {
  landing: 'text-blue-400 bg-blue-500/10',
  email: 'text-purple-400 bg-purple-500/10',
  whatsapp: 'text-green-400 bg-green-500/10',
  ad_copy: 'text-orange-400 bg-orange-500/10',
  creative: 'text-pink-400 bg-pink-500/10',
  analysis: 'text-cyan-400 bg-cyan-500/10',
  video_script: 'text-red-400 bg-red-500/10',
  avatar_script: 'text-teal-400 bg-teal-500/10',
  brief_guide: 'text-yellow-400 bg-yellow-500/10',
};

const CATEGORY_LABELS = {
  landing: 'Landings',
  email: 'Emails',
  whatsapp: 'WhatsApp',
  ad_copy: 'Ad Copy',
  creative: 'Creativos',
  analysis: 'Análisis',
  video_script: 'Guiones',
  avatar_script: 'Avatar',
  brief_guide: 'Guías Brief',
};

const AdminPromptsDynamic = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  // Data state
  const [assetTypes, setAssetTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [variables, setVariables] = useState([]);
  
  // Use FUNNEL_STRATEGIES directly for strategies (they have variants)
  const strategies = FUNNEL_STRATEGIES;
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('landing');
  const [selectedStrategy, setSelectedStrategy] = useState('*');
  const [selectedVariant, setSelectedVariant] = useState('todas');
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [promptContent, setPromptContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedVar, setCopiedVar] = useState(null);
  const [showNewAssetModal, setShowNewAssetModal] = useState(false);
  const [newAsset, setNewAsset] = useState({ id: '', label: '', category: 'landing', description: '' });
  
  // AI Generation state
  const [generatingAI, setGeneratingAI] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, currentAsset: '' });
  
  // Creative subsections state (for creative assets only)
  const [isCreativeAsset, setIsCreativeAsset] = useState(false);
  const [creativeSubsections, setCreativeSubsections] = useState({
    visual_concept: '',
    copy_text: '',
    safe_text_instructions: '',
    brand_instructions: ''
  });
  const [subsectionLabels, setSubsectionLabels] = useState({});
  const [activeSubsection, setActiveSubsection] = useState('visual_concept');
  const [savingSubsection, setSavingSubsection] = useState(null);
  
  // Creative asset IDs
  const CREATIVE_ASSET_IDS = [
    "ad_creative_imagen", "ad_creative_video", "ad_creative_story",
    "ad_creative_reel", "ad_creative_carrusel", "ad_creative_avatar_ia", 
    "ad_creative_voz_ia"
  ];
  
  // Derived state for selectedAsset
  const selectedAsset = useMemo(() => {
    return assetTypes.find(a => a.id === selectedAssetId) || null;
  }, [assetTypes, selectedAssetId]);

  const isAuthorized = user?.role === 'super_admin';

  // Build variants map from FUNNEL_STRATEGIES
  const variantsByStrategy = useMemo(() => {
    const map = {};
    FUNNEL_STRATEGIES.forEach(strategy => {
      const variants = [
        { id: 'todas', nombre: `Todas las variantes de ${strategy.label}` },
        ...(strategy.variants || []).map(v => ({ id: v.id, nombre: v.label }))
      ];
      map[strategy.id] = variants;
    });
    return map;
  }, []);

  // Explicit landing names per strategy/variant as specified
  // These names match exactly what we want to show in the UI
  const landingsPorVariante = useMemo(() => ({
    lanzamiento: {
      '1-directo': ['Página de Registro / Opt-in', 'Página Puente', 'Página Gracias (Lead)', 'Sala del Directo / Webinar', 'Página de Ventas', 'Página de Checkout', 'Gracias por Compra'],
      '2-directos': ['Página de Registro / Opt-in', 'Página Puente', 'Página Gracias (Lead)', 'Sala del Directo / Webinar', 'Sala Directo 2 / Ventas', 'Página de Ventas', 'Página de Checkout', 'Gracias por Compra'],
      '3-directos': ['Página de Registro / Opt-in', 'Página Puente', 'Página Gracias (Lead)', 'Sala Live 1', 'Sala Live 2', 'Sala Live 3 / Ventas', 'Página de Ventas', 'Página de Checkout', 'Gracias por Compra'],
      'serie-lives': ['Página de Aviso / Opt-in', 'Página Puente', 'Página de Ventas', 'Gracias por Compra'],
      'todas': [] // empty means show all strategy landings combined
    },
    evergreen: {
      'vsl': ['Página de Registro / Opt-in', 'Página Puente', 'Página Gracias (Lead)', 'Página VSL', 'Página de Ventas', 'Página de Checkout', 'Gracias por Compra'],
      'webinar-automatizado': ['Página de Registro / Opt-in', 'Página Puente', 'Página Gracias (Lead)', 'Sala del Directo / Webinar', 'Página de Ventas', 'Página de Checkout', 'Gracias por Compra'],
      'tripwire': ['Página de Registro / Opt-in', 'Página de Ventas', 'Página de Checkout', 'Página de Upsell', 'Página de Downsell', 'Gracias por Compra'],
      'quiz-funnel': ['Quiz - Inicio', 'Quiz - Resultado', 'Página de Ventas', 'Página de Checkout', 'Gracias por Compra'],
      'todas': []
    },
    plf: {
      '4-videos': ['Página de Registro / Opt-in', 'Página Puente', 'Página Gracias (Lead)', 'Video 1', 'Video 2', 'Video 3', 'Video 4', 'Página de Ventas', 'Página de Checkout', 'Gracias por Compra'],
      '3-videos': ['Página de Registro / Opt-in', 'Página Puente', 'Página Gracias (Lead)', 'Video 1', 'Video 2', 'Video 3', 'Página de Ventas', 'Página de Checkout', 'Gracias por Compra'],
      'todas': []
    },
    challenge: {
      '5-dias': ['Página de Registro / Opt-in', 'Página Puente', 'Página Gracias (Lead)', 'Página de Ventas', 'Página de Checkout', 'Gracias por Compra'],
      '7-dias': ['Página de Registro / Opt-in', 'Página Puente', 'Página Gracias (Lead)', 'Página de Ventas', 'Página de Checkout', 'Gracias por Compra'],
      '21-dias': ['Página de Registro / Opt-in', 'Página Puente', 'Página Gracias (Lead)', 'Página de Ventas', 'Página de Checkout', 'Gracias por Compra'],
      'todas': []
    },
    high_ticket: {
      'aplicacion': ['Página de Registro / Opt-in', 'Página Puente', 'Página Gracias (Lead)', 'Página de Ventas'],
      'llamada': ['Página de Registro / Opt-in', 'Página Puente', 'Página Gracias (Lead)'],
      'vsl-llamada': ['Página de Registro / Opt-in', 'Página Puente', 'Página Gracias (Lead)', 'Página VSL'],
      'todas': []
    }
  }), []);

  // Direct mapping from landing names (used in UI/spec) to asset IDs
  // Each landing name maps to the exact asset IDs that should be shown
  // IDs must match the asset_types in the database
  const landingToAssetIds = useMemo(() => ({
    // Registro variants
    'Página de Registro / Opt-in': ['registro'],
    'Página de Registro': ['registro'],
    
    // Aviso (for serie-lives)
    'Página de Aviso / Opt-in': ['aviso'],
    'Página de Aviso': ['aviso'],
    'Página de Aviso (Opt-in)': ['aviso'],
    
    // Puente
    'Página Puente': ['pagina_puente'],
    'Página Puente (Únete a WA)': ['pagina_puente'],
    
    // Gracias Lead
    'Página Gracias (Lead)': ['gracias_lead'],
    'Página Gracias': ['gracias_lead'],
    'Página Gracias (Pixel Lead)': ['gracias_lead'],
    
    // Salas de Directo/Webinar
    'Sala del Directo / Webinar': ['sala_directo'],
    'Sala del Directo': ['sala_directo'],
    'Sala del Directo / Replay': ['sala_directo', 'replay'],
    
    // Sala Directo 2
    'Sala Directo 2 / Ventas': ['sala_directo_2'],
    'Sala Directo 2': ['sala_directo_2'],
    
    // Salas Live
    'Sala Live 1': ['sala_live_1'],
    'Sala Live 2': ['sala_live_2'],
    'Sala Live 3 / Ventas': ['sala_live_3'],
    'Sala Live 3': ['sala_live_3'],
    
    // Ventas y Checkout
    'Página de Ventas': ['ventas'],
    'Página de Checkout': ['checkout'],
    
    // Gracias Compra
    'Gracias por Compra': ['gracias_compra'],
    'Gracias por Compra (Pixel Purchase)': ['gracias_compra'],
    
    // VSL
    'Página VSL': ['vsl_page'],
    'VSL Page': ['vsl_page'],
    
    // Tripwire/Upsell/Downsell
    'Página de Oferta Tripwire': ['ventas'],
    'Página de Upsell': ['upsell'],
    'Upsell 1': ['upsell'],
    'Página de Downsell': ['downsell'],
    'Downsell': ['downsell'],
    
    // Quiz
    'Quiz - Inicio': ['quiz_inicio'],
    'Página de Quiz': ['quiz_inicio'],
    'Quiz - Resultado': ['quiz_resultado'],
    'Página de Resultados': ['quiz_resultado'],
    
    // PLF Videos
    'Video 1': ['sala_directo'],
    'Video 2': ['sala_directo'],
    'Video 3': ['sala_directo'],
    'Video 4': ['sala_directo'],
    
    // Replay
    'Página de Replay': ['replay'],
  }), []);

  // Get current variants for selected strategy
  const currentVariants = selectedStrategy !== '*' ? (variantsByStrategy[selectedStrategy] || []) : [];

  // Build the complete strategy_id for API calls (e.g., "lanzamiento_2-directos")
  const getFullStrategyId = useCallback(() => {
    if (selectedStrategy === '*') return '*';
    return `${selectedStrategy}_${selectedVariant}`;
  }, [selectedStrategy, selectedVariant]);

  const fetchData = useCallback(async () => {
    try {
      const [assetRes, varRes] = await Promise.all([
        api.get('/admin/asset-types'),
        api.get('/admin/prompts/variables')
      ]);
      
      setAssetTypes(assetRes.data.asset_types || []);
      setCategories(assetRes.data.categories || []);
      setVariables(varRes.data.variables || []);
      
      // Select first asset of first category
      const firstCatAssets = assetRes.data.by_category?.landing || [];
      if (firstCatAssets.length > 0) {
        setSelectedAssetId(firstCatAssets[0]?.id);
      }
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Fetch prompt template when asset, strategy, or variant changes
  // For creative assets, fetch subsections instead
  useEffect(() => {
    const fetchTemplate = async () => {
      if (!selectedAssetId) return;
      
      const fullStrategyId = selectedStrategy === '*' ? '*' : `${selectedStrategy}_${selectedVariant}`;
      
      // Check if this is a creative asset
      const isCreative = CREATIVE_ASSET_IDS.includes(selectedAssetId);
      setIsCreativeAsset(isCreative);
      
      if (isCreative) {
        // Fetch creative subsections
        try {
          const response = await api.get(`/admin/creative-prompts/${selectedAssetId}/${fullStrategyId}`);
          setCreativeSubsections(response.data.subsections || {
            visual_concept: '',
            copy_text: '',
            safe_text_instructions: '',
            brand_instructions: ''
          });
          setSubsectionLabels(response.data.subsection_labels || {});
          setActiveSubsection('visual_concept');
          // Also set promptContent for backward compatibility
          setPromptContent(response.data.subsections?.visual_concept || '');
        } catch (error) {
          console.error('Error fetching creative subsections:', error);
          setCreativeSubsections({
            visual_concept: '',
            copy_text: '',
            safe_text_instructions: '',
            brand_instructions: ''
          });
        }
      } else {
        // Regular prompt template
        try {
          const response = await api.get(`/admin/prompt-templates/${selectedAssetId}/${fullStrategyId}`);
          setPromptContent(response.data.content || '');
        } catch (error) {
          console.error('Error fetching template:', error);
          setPromptContent('');
        }
      }
    };
    
    fetchTemplate();
  }, [api, selectedAssetId, selectedStrategy, selectedVariant, CREATIVE_ASSET_IDS]);

  // Initial data load
  useEffect(() => {
    if (isAuthorized) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isAuthorized, fetchData]);

  // Get filtered assets based on category, search, and strategy/variant for landings
  const filteredAssets = useMemo(() => {
    let assets = assetTypes.filter(a => 
      a.category === selectedCategory &&
      (searchQuery === '' || a.label.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    
    // For landing category with specific strategy and variant, filter by relevant landings
    // If variant is "todas" or strategy is "*", show all assets in the category
    if (selectedCategory === 'landing' && selectedStrategy !== '*' && selectedVariant !== 'todas') {
      const strategyConfig = landingsPorVariante[selectedStrategy];
      if (strategyConfig && strategyConfig[selectedVariant]) {
        const relevantLandings = strategyConfig[selectedVariant];
        
        if (relevantLandings && relevantLandings.length > 0) {
          // Build set of asset IDs that should be shown
          const allowedAssetIds = new Set();
          relevantLandings.forEach(landing => {
            const assetIds = landingToAssetIds[landing];
            if (assetIds) {
              assetIds.forEach(id => allowedAssetIds.add(id));
            }
          });
          
          // If we have specific IDs, filter by them
          if (allowedAssetIds.size > 0) {
            assets = assets.filter(asset => allowedAssetIds.has(asset.id));
          }
        }
      }
    }
    
    return assets;
  }, [assetTypes, selectedCategory, searchQuery, selectedStrategy, selectedVariant, landingsPorVariante, landingToAssetIds]);

  // Reset selected asset when it's no longer in filtered list
  // ONLY if user is filtering/searching, not when clicking on an asset directly
  useEffect(() => {
    // Only auto-select if there's no asset selected
    if (!selectedAsset && filteredAssets.length > 0) {
      setSelectedAssetId(filteredAssets[0]?.id);
    }
  }, [filteredAssets, selectedAsset]);

  // Get strategy and variant labels for display (must be before conditional return)
  const getStrategyLabel = useCallback(() => {
    const strategy = FUNNEL_STRATEGIES.find(s => s.id === selectedStrategy);
    return strategy?.label || selectedStrategy;
  }, [selectedStrategy]);

  const getVariantLabel = useCallback(() => {
    const strategy = FUNNEL_STRATEGIES.find(s => s.id === selectedStrategy);
    const variant = strategy?.variants?.find(v => v.id === selectedVariant);
    return variant?.label || selectedVariant;
  }, [selectedStrategy, selectedVariant]);

  // Check if AI generation is available (specific strategy + variant + asset selected)
  const canGenerateAI = selectedStrategy !== '*' && selectedVariant !== 'todas' && selectedAsset;

  if (!isAuthorized && !loading) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSavePrompt = async () => {
    if (!selectedAsset) return;
    
    const fullStrategyId = selectedStrategy === '*' ? '*' : `${selectedStrategy}_${selectedVariant}`;
    
    setSaving(true);
    try {
      await api.put(`/admin/prompt-templates/${selectedAsset.id}/${fullStrategyId}`, {
        content: promptContent
      });
      toast.success('Prompt guardado correctamente');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Save creative subsection
  const handleSaveCreativeSubsection = async (subsection) => {
    if (!selectedAsset || !isCreativeAsset) return;
    
    const fullStrategyId = selectedStrategy === '*' ? '*' : `${selectedStrategy}_${selectedVariant}`;
    
    setSavingSubsection(subsection);
    try {
      await api.put(`/admin/creative-prompts/${selectedAsset.id}/${fullStrategyId}/${subsection}`, {
        subsection,
        content: creativeSubsections[subsection]
      });
      toast.success(`${subsectionLabels[subsection] || subsection} guardado`);
    } catch (error) {
      toast.error('Error al guardar subsección');
    } finally {
      setSavingSubsection(null);
    }
  };

  // Update creative subsection content
  const handleSubsectionChange = (subsection, content) => {
    setCreativeSubsections(prev => ({
      ...prev,
      [subsection]: content
    }));
  };

  // Generate prompt with AI
  const handleGenerateWithAI = async () => {
    if (!selectedAsset || selectedStrategy === '*' || selectedVariant === 'todas') {
      toast.error('Selecciona una estrategia, variante y activo específicos');
      return;
    }
    
    setGeneratingAI(true);
    try {
      const response = await api.post('/admin/prompts/generate', {
        strategy_id: selectedStrategy,
        variant_id: selectedVariant,
        asset_type: selectedAsset.category,
        asset_name: selectedAsset.label
      });
      
      if (response.data.success && response.data.prompt) {
        setPromptContent(response.data.prompt);
        toast.success('Prompt generado — revísalo y pulsa Guardar');
      } else {
        throw new Error('No se recibió el prompt');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error generating prompt:', error);
      toast.error('Error al generar el prompt — inténtalo de nuevo');
    } finally {
      setGeneratingAI(false);
    }
  };

  // Generate all prompts for variant (batch)
  const handleBatchGenerate = async () => {
    if (selectedStrategy === '*' || selectedVariant === 'todas') {
      toast.error('Selecciona una estrategia y variante específicas');
      return;
    }
    
    setShowBatchModal(false);
    setBatchGenerating(true);
    
    // Get filtered assets for this category
    const assetsToGenerate = assetTypes.filter(a => a.category === selectedCategory);
    const total = assetsToGenerate.length;
    
    setBatchProgress({ current: 0, total, currentAsset: '' });
    
    let successCount = 0;
    let errorCount = 0;
    
    for (let i = 0; i < assetsToGenerate.length; i++) {
      const asset = assetsToGenerate[i];
      setBatchProgress({ current: i + 1, total, currentAsset: asset.label });
      
      try {
        const response = await api.post('/admin/prompts/generate', {
          strategy_id: selectedStrategy,
          variant_id: selectedVariant,
          asset_type: asset.category,
          asset_name: asset.label
        });
        
        if (response.data.success && response.data.prompt) {
          // Save the generated prompt
          const fullStrategyId = `${selectedStrategy}_${selectedVariant}`;
          await api.put(`/admin/prompt-templates/${asset.id}/${fullStrategyId}`, {
            content: response.data.prompt
          });
          successCount++;
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error(`Error generating prompt for ${asset.label}:`, error);
        errorCount++;
      }
      
      // Small delay between requests to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setBatchGenerating(false);
    setBatchProgress({ current: 0, total: 0, currentAsset: '' });
    
    if (errorCount === 0) {
      toast.success(`✅ ${successCount} prompts generados y guardados correctamente`);
    } else {
      toast.warning(`${successCount} prompts generados, ${errorCount} errores`);
    }
    
    // Refresh current prompt by triggering re-render
    // Force refresh by setting a new object reference
    if (selectedAsset) {
      setSelectedAssetId(selectedAsset?.id);
    }
  };

  const handleResetPrompt = async () => {
    if (!selectedAsset) return;
    
    const fullStrategyId = selectedStrategy === '*' ? '*' : `${selectedStrategy}_${selectedVariant}`;
    
    if (!window.confirm('¿Restaurar este prompt a su valor por defecto?')) return;
    
    try {
      await api.delete(`/admin/prompt-templates/${selectedAsset.id}/${fullStrategyId}`);
      toast.success('Prompt restaurado');
      // Force refresh
      setSelectedAssetId(selectedAsset?.id);
    } catch (error) {
      // If 404, it's already default - just refresh
      setSelectedAssetId(selectedAsset?.id);
    }
  };

  const handleCreateAssetType = async () => {
    if (!newAsset.id || !newAsset.label) {
      toast.error('ID y nombre son requeridos');
      return;
    }
    
    try {
      await api.post('/admin/asset-types', newAsset);
      toast.success('Tipo de activo creado');
      setShowNewAssetModal(false);
      setNewAsset({ id: '', label: '', category: 'landing', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear');
    }
  };

  const handleDeleteAssetType = async (assetId) => {
    if (!window.confirm('¿Eliminar este tipo de activo? También se eliminarán sus prompts asociados.')) return;
    
    try {
      await api.delete(`/admin/asset-types/${assetId}`);
      toast.success('Tipo de activo eliminado');
      fetchData();
      if (selectedAsset?.id === assetId) {
        setSelectedAssetId(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const copyVariable = (varKey) => {
    navigator.clipboard.writeText(varKey);
    setCopiedVar(varKey);
    setTimeout(() => setCopiedVar(null), 2000);
  };

  // Build display label for current selection
  const selectedStrategyLabel = selectedStrategy === '*' 
    ? '🌐 Global (Todas las estrategias)'
    : strategies.find(s => s.id === selectedStrategy)?.label || selectedStrategy;
  const selectedVariantLabel = currentVariants.find(v => v.id === selectedVariant)?.nombre || selectedVariant;
  const fullSelectionLabel = selectedStrategy === '*' 
    ? selectedStrategyLabel 
    : `${selectedStrategyLabel} → ${selectedVariantLabel}`;

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="admin-prompts-dynamic">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand to-purple-600 flex items-center justify-center">
              <Wand2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold text-text-main">Prompts Maestros</h1>
              <p className="text-text-secondary text-xs sm:text-sm">
                Configura los prompts de IA por tipo de activo y estrategia
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/prompt-stats')}
              className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 flex-1 sm:flex-none"
              data-testid="prompt-stats-btn"
            >
              <BarChart3 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Estadísticas</span>
            </Button>
            <Button
              onClick={() => setShowNewAssetModal(true)}
              className="bg-brand-500 hover:bg-brand-hover text-white flex-1 sm:flex-none"
              data-testid="add-asset-type-btn"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Nuevo Tipo</span>
            </Button>
          </div>
        </div>

        {/* Main Content - Responsive Grid */}
        <div className="flex-1 flex flex-col lg:grid lg:grid-cols-12 gap-4 min-h-0">
          {/* Left Panel - Category & Asset List */}
          <div className="lg:col-span-3 flex flex-col bg-surface border border-border-subtle rounded-xl overflow-hidden min-h-[300px] lg:min-h-0">
            {/* Category Tabs - Horizontal scroll on mobile */}
            <div className="flex border-b border-border-subtle overflow-x-auto">
              {categories.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.id] || FileText;
                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setSelectedCategory(cat.id);
                      setSelectedAssetId(null);
                    }}
                    className={`flex-1 min-w-[60px] px-3 py-3 flex flex-col items-center gap-1 transition-colors ${
                      selectedCategory === cat.id 
                        ? 'bg-brand/10 border-b-2 border-brand' 
                        : 'hover:bg-app-bg'
                    }`}
                    title={cat.label}
                  >
                    <Icon className={`w-4 h-4 ${selectedCategory === cat.id ? 'text-brand' : 'text-text-muted'}`} />
                    <span className={`text-[10px] truncate w-full text-center ${selectedCategory === cat.id ? 'text-brand' : 'text-text-muted'}`}>
                      {cat.label.split(' ')[1]}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <div className="p-3 border-b border-border-subtle">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar..."
                  className="pl-9 bg-app-bg border-border-subtle text-sm"
                />
              </div>
            </div>

            {/* Asset List */}
            <div className="flex-1 overflow-y-auto p-2">
              {filteredAssets.length === 0 ? (
                <p className="text-center text-text-muted text-sm py-4">No hay activos en esta categoría</p>
              ) : (
                filteredAssets.map((asset) => (
                  <button
                    key={asset.id}
                    onClick={() => setSelectedAssetId(asset.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg mb-1 transition-colors text-left group ${
                      selectedAsset?.id === asset.id
                        ? 'bg-brand/10 border border-brand/30'
                        : 'hover:bg-app-bg border border-transparent'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${
                        selectedAsset?.id === asset.id ? 'text-brand' : 'text-text-main'
                      }`}>
                        {asset.label}
                      </p>
                      {asset.description && (
                        <p className="text-xs text-text-muted truncate">{asset.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {asset.is_custom && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAssetType(asset.id);
                          }}
                          className="p-1 opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity"
                          title="Eliminar"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                      <ChevronRight className={`w-4 h-4 ${
                        selectedAsset?.id === asset.id ? 'text-brand' : 'text-text-muted'
                      }`} />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Center Panel - Prompt Editor */}
          <div className="lg:col-span-6 flex flex-col bg-surface border border-border-subtle rounded-xl overflow-hidden min-h-[400px] lg:min-h-0">
            {selectedAsset ? (
              <>
                {/* Editor Header */}
                <div className="p-3 sm:p-4 border-b border-border-subtle">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <h2 className="font-medium text-text-main truncate">{selectedAsset.label}</h2>
                      <p className="text-xs text-text-muted truncate">{selectedAsset.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleResetPrompt}
                        className="border-border-subtle text-text-muted hover:text-text-main"
                      >
                        <RotateCcw className="w-3 h-3 sm:mr-1" />
                        <span className="hidden sm:inline">Restaurar</span>
                      </Button>
                      {canGenerateAI && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleGenerateWithAI}
                          disabled={generatingAI}
                          className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 hover:text-purple-300"
                          data-testid="generate-ai-btn"
                        >
                          {generatingAI ? (
                            <>
                              <Loader2 className="w-3 h-3 sm:mr-1 animate-spin" />
                              <span className="hidden sm:inline">Generando...</span>
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-3 h-3 sm:mr-1" />
                              <span className="hidden sm:inline">Generar IA</span>
                            </>
                          )}
                        </Button>
                      )}
                      {/* Regular save button - only for non-creative assets */}
                      {!isCreativeAsset && (
                        <Button
                          size="sm"
                          onClick={handleSavePrompt}
                          disabled={saving}
                          className="bg-brand-500 hover:bg-brand-hover text-white"
                          data-testid="save-prompt-btn"
                        >
                          <Save className="w-3 h-3 sm:mr-1" />
                          <span className="hidden sm:inline">{saving ? 'Guardando...' : 'Guardar'}</span>
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Strategy Selector */}
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-text-muted whitespace-nowrap hidden sm:inline">Estrategia:</Label>
                    <select
                      value={selectedStrategy}
                      onChange={(e) => {
                        setSelectedStrategy(e.target.value);
                        setSelectedVariant('todas'); // Reset variant when strategy changes
                      }}
                      className="flex-1 bg-app-bg border border-border-subtle rounded-lg px-3 py-1.5 text-sm text-text-main"
                      data-testid="strategy-selector"
                    >
                      <option value="*">🌐 Global (Todas)</option>
                      {strategies.map((s) => (
                        <option key={s.id} value={s.id}>{s.icon} {s.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Variant Selector - Only visible for non-Global strategies */}
                  {selectedStrategy !== '*' && currentVariants.length > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <Label className="text-xs text-text-muted whitespace-nowrap hidden sm:inline">Variante:</Label>
                      <select
                        value={selectedVariant}
                        onChange={(e) => setSelectedVariant(e.target.value)}
                        className="flex-1 bg-app-bg border border-border-subtle rounded-lg px-3 py-1.5 text-sm text-text-main"
                        data-testid="variant-selector"
                      >
                        {currentVariants.map((v) => (
                          <option key={v.id} value={v.id}>{v.nombre}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Batch Generate Button */}
                  {selectedStrategy !== '*' && selectedVariant !== 'todas' && (
                    <div className="mt-3 pt-3 border-t border-border-subtle">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowBatchModal(true)}
                        disabled={batchGenerating}
                        className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                        data-testid="batch-generate-btn"
                      >
                        {batchGenerating ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                            Generando {batchProgress.current} de {batchProgress.total}...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-3 h-3 mr-2" />
                            Generar todos los prompts
                          </>
                        )}
                      </Button>
                      {batchGenerating && (
                        <div className="mt-2">
                          <Progress value={(batchProgress.current / batchProgress.total) * 100} className="h-1" />
                          <p className="text-[10px] text-text-muted mt-1 truncate">
                            {batchProgress.currentAsset}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Editor Body */}
                <div className="flex-1 p-4 overflow-hidden">
                  {isCreativeAsset ? (
                    /* Creative Subsections Editor */
                    <div className="h-full flex flex-col" data-testid="creative-subsections-editor">
                      {/* Subsection Tabs */}
                      <div className="flex gap-1 mb-3 border-b border-border-subtle pb-2 overflow-x-auto">
                        {Object.entries(subsectionLabels).map(([key, label]) => (
                          <button
                            key={key}
                            onClick={() => setActiveSubsection(key)}
                            className={`px-3 py-1.5 text-xs rounded-lg whitespace-nowrap transition-all ${
                              activeSubsection === key
                                ? 'bg-brand text-white font-medium'
                                : 'bg-app-bg text-text-muted hover:bg-brand/10 hover:text-text-main'
                            }`}
                            data-testid={`subsection-tab-${key}`}
                          >
                            {key === 'visual_concept' && '🎨 '}
                            {key === 'copy_text' && '✍️ '}
                            {key === 'safe_text_instructions' && '📐 '}
                            {key === 'brand_instructions' && '🏷️ '}
                            {label}
                          </button>
                        ))}
                      </div>
                      
                      {/* Active Subsection Editor */}
                      <div className="flex-1 flex flex-col">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-text-muted">
                            {activeSubsection === 'visual_concept' && 'Describe el concepto visual del creativo'}
                            {activeSubsection === 'copy_text' && 'Define el texto que aparecerá en el creativo'}
                            {activeSubsection === 'safe_text_instructions' && 'Instrucciones para mantener el texto en zona segura'}
                            {activeSubsection === 'brand_instructions' && 'Colores, tipografía y estilo de marca'}
                          </span>
                          <Button
                            size="sm"
                            onClick={() => handleSaveCreativeSubsection(activeSubsection)}
                            disabled={savingSubsection === activeSubsection}
                            className="h-7 text-xs"
                            data-testid={`save-subsection-${activeSubsection}`}
                          >
                            {savingSubsection === activeSubsection ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <Save className="w-3 h-3 mr-1" />
                            )}
                            Guardar
                          </Button>
                        </div>
                        <Textarea
                          value={creativeSubsections[activeSubsection] || ''}
                          onChange={(e) => handleSubsectionChange(activeSubsection, e.target.value)}
                          placeholder={`Escribe las instrucciones para ${subsectionLabels[activeSubsection] || activeSubsection}...`}
                          className="flex-1 min-h-[200px] bg-app-bg border-border-subtle text-text-main font-mono text-sm resize-none"
                          data-testid={`subsection-textarea-${activeSubsection}`}
                        />
                        <div className="mt-2 text-xs text-text-muted">
                          {(creativeSubsections[activeSubsection] || '').length.toLocaleString()} caracteres
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Regular Prompt Editor */
                    <Textarea
                      value={promptContent}
                      onChange={(e) => setPromptContent(e.target.value)}
                      placeholder="Escribe el prompt para este tipo de activo..."
                      className="h-full min-h-[300px] bg-app-bg border-border-subtle text-text-main font-mono text-sm resize-none"
                      data-testid="prompt-editor"
                    />
                  )}
                </div>

                {/* Editor Footer */}
                <div className="px-4 py-2 border-t border-border-subtle flex items-center justify-between">
                  <span className="text-xs text-text-muted">
                    {isCreativeAsset ? (
                      <>4 subsecciones • Guardado individual por sección</>
                    ) : (
                      <>{promptContent.length.toLocaleString()} caracteres</>
                    )}
                  </span>
                  <span className="text-xs text-text-muted">
                    Prompt para: <span className="text-brand-500">{fullSelectionLabel}</span>
                  </span>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-text-muted">
                <FileText className="w-12 h-12 mb-3 opacity-50" />
                <p>Selecciona un tipo de activo para editar su prompt</p>
              </div>
            )}
          </div>

          {/* Right Panel - Variables */}
          <div className="lg:col-span-3 flex flex-col bg-surface border border-border-subtle rounded-xl overflow-hidden min-h-[250px] lg:min-h-0">
            <div className="p-3 sm:p-4 border-b border-border-subtle">
              <h3 className="font-medium text-text-main flex items-center gap-2 text-sm">
                <AlertCircle className="w-4 h-4 text-brand-500" />
                Variables Disponibles
              </h3>
              <p className="text-xs text-text-muted mt-1">
                Click para copiar. Se reemplazan con datos del brief.
              </p>
            </div>
            
            <div className="flex-1 overflow-y-auto p-2 max-h-[200px] lg:max-h-none">
              {/* Group variables by category */}
              {(() => {
                const categories = {
                  producto: { label: '📦 Producto', icon: '📦', variables: [] },
                  avatar: { label: '👤 Avatar', icon: '👤', variables: [] },
                  oferta: { label: '🎁 Oferta', icon: '🎁', variables: [] },
                  evento: { label: '📅 Evento', icon: '📅', variables: [] },
                  urgencia: { label: '⏰ Urgencia', icon: '⏰', variables: [] },
                  recursos: { label: '🔗 Recursos', icon: '🔗', variables: [] },
                  contexto: { label: '⚙️ Contexto', icon: '⚙️', variables: [] },
                };
                
                variables.forEach(v => {
                  const cat = v.category || 'contexto';
                  if (categories[cat]) {
                    categories[cat].variables.push(v);
                  } else {
                    categories.contexto.variables.push(v);
                  }
                });
                
                return Object.entries(categories)
                  .filter(([_, cat]) => cat.variables.length > 0)
                  .map(([key, cat]) => (
                    <div key={key} className="mb-3">
                      <div className="text-[10px] uppercase text-text-muted font-semibold px-2 py-1 bg-app-bg rounded mb-1">
                        {cat.label}
                      </div>
                      {cat.variables.map((variable) => (
                        <button
                          key={variable.key}
                          onClick={() => copyVariable(variable.key)}
                          className="w-full flex items-center justify-between p-1.5 rounded-lg hover:bg-brand/10 transition-colors group"
                          title={variable.description}
                        >
                          <div className="text-left overflow-hidden">
                            <code className="text-[11px] text-brand-500 font-mono">{variable.key}</code>
                            <p className="text-[9px] text-text-muted truncate">{variable.description}</p>
                          </div>
                          {copiedVar === variable.key ? (
                            <Check className="w-3 h-3 text-green-400 flex-shrink-0" />
                          ) : (
                            <Copy className="w-3 h-3 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  ));
              })()}
            </div>

            {/* Quick Tips */}
            <div className="p-3 border-t border-border-subtle bg-app-bg/50">
              <p className="text-xs text-text-muted">
                <strong className="text-text-main">Tip:</strong> Usa <code className="text-brand-500">{"{{brief_data}}"}</code> para insertar todos los datos del proyecto automáticamente.
              </p>
            </div>
          </div>
        </div>

        {/* New Asset Modal */}
        {showNewAssetModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-surface border border-border-subtle rounded-xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg font-semibold text-text-main">
                  Nuevo Tipo de Activo
                </h2>
                <button onClick={() => setShowNewAssetModal(false)} className="text-text-muted hover:text-text-main">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-text-secondary">ID único (sin espacios)</Label>
                  <Input
                    value={newAsset.id}
                    onChange={(e) => setNewAsset({ ...newAsset, id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                    placeholder="ej: email_upsell"
                    className="bg-app-bg border-border-subtle"
                  />
                </div>
                
                <div>
                  <Label className="text-text-secondary">Nombre</Label>
                  <Input
                    value={newAsset.label}
                    onChange={(e) => setNewAsset({ ...newAsset, label: e.target.value })}
                    placeholder="ej: Email de Upsell"
                    className="bg-app-bg border-border-subtle"
                  />
                </div>
                
                <div>
                  <Label className="text-text-secondary">Categoría</Label>
                  <select
                    value={newAsset.category}
                    onChange={(e) => setNewAsset({ ...newAsset, category: e.target.value })}
                    className="w-full bg-app-bg border border-border-subtle rounded-lg px-3 py-2 text-text-main"
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label className="text-text-secondary">Descripción (opcional)</Label>
                  <Input
                    value={newAsset.description}
                    onChange={(e) => setNewAsset({ ...newAsset, description: e.target.value })}
                    placeholder="ej: Email post-compra con oferta adicional"
                    className="bg-app-bg border-border-subtle"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowNewAssetModal(false)}
                  className="border-border-subtle"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreateAssetType}
                  className="bg-brand-500 hover:bg-brand-hover text-white"
                >
                  Crear Tipo de Activo
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Batch Generate Modal */}
        <Dialog open={showBatchModal} onOpenChange={setShowBatchModal}>
          <DialogContent className="bg-surface border-border-subtle">
            <DialogHeader>
              <DialogTitle className="text-text-main flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" />
                Generar todos los prompts
              </DialogTitle>
              <DialogDescription className="text-text-secondary">
                Se generarán <strong className="text-text-main">{assetTypes.filter(a => a.category === selectedCategory).length} prompts</strong> para:
                <br />
                <span className="text-brand-500 font-medium">{getStrategyLabel()} → {getVariantLabel()}</span>
                <br /><br />
                <span className="text-amber-400 text-sm">
                  ⚠️ Esto puede tardar varios minutos. Los prompts se guardarán automáticamente.
                </span>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4">
              <Button
                variant="ghost"
                onClick={() => setShowBatchModal(false)}
                className="text-text-muted hover:text-text-main"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleBatchGenerate}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="confirm-batch-generate-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generar Todos
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default AdminPromptsDynamic;
